<script setup lang="ts">
import {
	onBeforeUnmount, onMounted, ref,
} from "vue";

import { client } from "../api";
import PageShell from "../components/PageShell.vue";
import { type Handshake, returnToGarmin } from "../handshake";

const props = defineProps<{ handshake: Handshake }>();

type Mode = "choose" | "signin" | "signup";

/**
 * Nuxt UI renders the label as a bare text node and puts `text-inverted` on the button root,
 * which lands white on our yellow at about 1.8:1. The theme's `label` slot never applies to a
 * text-only button, so the house treatment has to go on the root.
 */
const CTA_CLASS = "font-cta tracking-wide sm:tracking-wider !text-brand-500";
const GHOST_CLASS = "font-cta tracking-wide sm:tracking-wider !text-white bg-transparent "
	+ "ring-1 ring-white/25 hover:bg-white/10";

const mode = ref<Mode>("choose");
const email = ref("");
const password = ref("");
const name = ref("");
const busy = ref(false);
const error = ref("");
const linked = ref(false);
/** A new account waiting for staff review; they still go back to Garmin to share aircraft. */
const pending = ref(false);
const RETURN_DELAY_SEC = 12;
const countdown = ref<number | null>(null);
let timer: ReturnType<typeof setInterval> | undefined;

const startReturnCountdown = () => {
	if (!props.handshake.returnUrl) {
		return;
	}
	countdown.value = RETURN_DELAY_SEC;
	timer = setInterval(() => {
		countdown.value = (countdown.value ?? 1) - 1;
		if (countdown.value <= 0) {
			clearInterval(timer);
			returnToGarmin(props.handshake.returnUrl!);
		}
	}, 1000);
};

onBeforeUnmount(() => clearInterval(timer));

onMounted(async () => {
	const res = await client.api.v1.garmin.connection.get({
		query: { mci: props.handshake.mci, handshakeQuery: props.handshake.rawQuery },
	});
	if (res.data?.claimed) {
		linked.value = true;
		pending.value = !!res.data.pending;
	}
});

const finish = () => {
	linked.value = true;
	if (props.handshake.returnUrl) {
		returnToGarmin(props.handshake.returnUrl);
	}
};

const signIn = async () => {
	busy.value = true;
	error.value = "";
	const session = await client.api.v1.sessions.post({
		email: email.value,
		password: password.value,
	});
	if (session.status !== 200) {
		error.value = session.status === 401
			? "That email and password didn't match an account."
			: "Something went wrong signing in. Please try again.";
		busy.value = false;
		return;
	}
	const link = await client.api.v1.garmin.link.post({ mci: props.handshake.mci, handshakeQuery: props.handshake.rawQuery });
	busy.value = false;
	if (link.status !== 200) {
		error.value = link.error?.value?.message ?? "Could not connect this Garmin account.";
		return;
	}
	finish();
};

const signUp = async () => {
	busy.value = true;
	error.value = "";
	const res = await client.api.v1.garmin.signup.post({
		mci: props.handshake.mci,
		email: email.value,
		name: name.value || undefined,
		handshakeQuery: props.handshake.rawQuery,
	});
	busy.value = false;
	if (res.status !== 200) {
		error.value = res.error?.value?.message ?? "Could not create your account.";
		if (res.error?.value?.reason === "account_exists") {
			mode.value = "signin";
		}
		return;
	}
	linked.value = true;
	pending.value = true;
	startReturnCountdown();
};
</script>

<template>
  <PageShell>
    <template v-if="linked && pending">
      <h1 class="font-serif text-2xl sm:text-4xl md:text-5xl leading-tight text-center max-w-3xl mx-auto text-brand-500">
        Welcome to
        <span class="italic whitespace-nowrap underline decoration-primary-500 decoration-4 underline-offset-4">Class A</span>
      </h1>
      <p class="mt-6 text-lg text-brand-300 text-center max-w-xl mx-auto">
        Thanks for signing up. Our team is reviewing your account, and we'll email you your
        sign-in details as soon as you're approved.
      </p>
      <p class="mt-4 text-lg text-brand-300 text-center max-w-xl mx-auto">
        In the meantime, head back to Garmin and choose the aircraft you'd like to share.
        We'll start analyzing their flights once your account is approved.
      </p>
      <div class="mt-10 flex flex-col items-center gap-3">
        <template v-if="props.handshake.returnUrl">
          <UButton
            size="xl"
            :class="CTA_CLASS"
            trailing-icon="ic-round-arrow-outward"
            @click="returnToGarmin(props.handshake.returnUrl!)"
          >
            Continue to Garmin
          </UButton>
          <p
            v-if="countdown !== null"
            class="text-sm text-brand-300"
          >
            Taking you back to Garmin in {{ countdown }}s
          </p>
        </template>
        <p
          v-else
          class="text-brand-300 text-center max-w-xl"
        >
          Head back to flyGarmin to choose the aircraft you'd like to share.
        </p>
      </div>
    </template>

    <template v-else-if="linked">
      <h1 class="font-serif text-2xl sm:text-4xl md:text-5xl leading-tight text-center max-w-3xl mx-auto text-brand-500">
        Your Garmin account is
        <span class="italic whitespace-nowrap underline decoration-primary-500 decoration-4 underline-offset-4">connected</span>
      </h1>
      <p class="mt-6 text-lg text-brand-300 text-center max-w-xl mx-auto">
        Pick the aircraft you want to share on the Garmin sharing page. Their flight data
        starts reaching Class A the moment you do.
      </p>
      <div class="mt-10 flex justify-center">
        <UButton
          v-if="props.handshake.returnUrl"
          size="xl"
          :class="CTA_CLASS"
          trailing-icon="ic-round-arrow-outward"
          @click="returnToGarmin(props.handshake.returnUrl!)"
        >
          Return to Garmin
        </UButton>
        <!-- No usable returnUrl means Garmin cannot finalize the connection from here. -->
        <p
          v-else
          class="text-brand-300 text-center max-w-xl"
        >
          {{ props.handshake.rejectedReturnUrl
            ? "The return link Garmin sent does not point back to a Garmin site, so we did not follow it. Head back to flyGarmin to finish setting up sharing."
            : "Garmin did not send a return link with this connection. Head back to flyGarmin to finish setting up sharing." }}
        </p>
      </div>
    </template>

    <template v-else>
      <h1 class="font-serif text-2xl sm:text-4xl md:text-5xl leading-tight text-center max-w-3xl mx-auto text-brand-500">
        Connect Garmin to
        <span class="italic whitespace-nowrap underline decoration-primary-500 decoration-4 underline-offset-4">Class A</span>
      </h1>
      <p class="mt-6 text-lg text-brand-300 text-center max-w-xl mx-auto">
        Link your account and your flight data starts working for you.
      </p>

      <div class="max-w-lg mx-auto bg-gray-900 p-6 sm:p-8 mt-10">
        <template v-if="mode === 'choose'">
          <h2 class="font-serif text-2xl text-white mb-6">
            Let's get you connected
          </h2>
          <div class="space-y-3">
            <UButton
              block
              size="xl"
              :class="CTA_CLASS"
              @click="mode = 'signin'"
            >
              I have a Class A account
            </UButton>
            <UButton
              block
              size="xl"
              variant="ghost"
              :class="GHOST_CLASS"
              @click="mode = 'signup'"
            >
              I'm new to Class A
            </UButton>
          </div>
        </template>

        <template v-else-if="mode === 'signin'">
          <h2 class="font-serif text-2xl text-white mb-6">
            Sign in
          </h2>
          <form
            class="space-y-4"
            @submit.prevent="signIn"
          >
            <UFormField
              label="Email"
              name="email"
              required
              :ui="{ label: 'text-white' }"
            >
              <UInput
                v-model="email"
                type="email"
                size="xl"
                autocomplete="email"
                required
                class="w-full"
              />
            </UFormField>
            <UFormField
              label="Password"
              name="password"
              required
              :ui="{ label: 'text-white' }"
            >
              <UInput
                v-model="password"
                type="password"
                size="xl"
                autocomplete="current-password"
                required
                class="w-full"
              />
            </UFormField>
            <UButton
              type="submit"
              block
              size="xl"
              :class="[CTA_CLASS, 'mt-2']"
              :loading="busy"
            >
              Sign in and connect
            </UButton>
          </form>
        </template>

        <template v-else>
          <h2 class="font-serif text-2xl text-white mb-3">
            Create your account
          </h2>
          <p class="text-gray-400 mb-6">
            We'll link your Garmin connection to it. Our team reviews every new account and
            emails your sign-in details once you're approved.
          </p>
          <form
            class="space-y-4"
            @submit.prevent="signUp"
          >
            <UFormField
              label="Name"
              name="name"
              :ui="{ label: 'text-white' }"
            >
              <UInput
                v-model="name"
                size="xl"
                autocomplete="name"
                class="w-full"
              />
            </UFormField>
            <UFormField
              label="Email"
              name="email"
              required
              :ui="{ label: 'text-white' }"
            >
              <UInput
                v-model="email"
                type="email"
                size="xl"
                autocomplete="email"
                required
                class="w-full"
              />
            </UFormField>
            <UButton
              type="submit"
              block
              size="xl"
              :class="[CTA_CLASS, 'mt-2']"
              :loading="busy"
            >
              Create account and connect
            </UButton>
          </form>
        </template>

        <UButton
          v-if="mode !== 'choose'"
          variant="link"
          color="neutral"
          class="mt-4 px-0 text-gray-400 hover:text-white [&_svg]:!text-gray-400"
          leading-icon="ic-round-arrow-back"
          @click="mode = 'choose'; error = ''"
        >
          Back
        </UButton>

        <UAlert
          v-if="error"
          class="mt-6"
          color="error"
          variant="subtle"
          :description="error"
        />
      </div>
    </template>
  </PageShell>
</template>
